import React, { useState, useEffect, useCallback } from "react";
import { BookOpen, Plus, Trash2, Loader2, AlertCircle } from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";
import { journalEntriesApi, chartOfAccountsApi } from "../../services/api";

interface ChartAccount {
  _id: string;
  code: string;
  name: string;
  type: string;
}

interface JournalLineForm {
  accountId: string;
  debit: string;
  credit: string;
  memo: string;
}

interface NewJournalEntryModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const emptyLine = (): JournalLineForm => ({
  accountId: "",
  debit: "",
  credit: "",
  memo: "",
});

const sourceTypeOptions = [
  { value: "manual", label: "Manual" },
  { value: "adjustment", label: "Adjustment" },
  { value: "correction", label: "Correction" },
];

const NewJournalEntryModal: React.FC<NewJournalEntryModalProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [memo, setMemo] = useState("");
  const [sourceType, setSourceType] = useState("manual");
  const [lines, setLines] = useState<JournalLineForm[]>([emptyLine(), emptyLine()]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Load chart of accounts on mount
  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoadingAccounts(true);
      try {
        const res = await chartOfAccountsApi.list();
        const flat = res.data?.flat || [];
        setAccounts(flat.filter((a: any) => a.active !== false));
      } catch (err) {
        console.error("Failed to load chart of accounts:", err);
      } finally {
        setLoadingAccounts(false);
      }
    };
    load();
  }, [open]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setMemo("");
      setSourceType("manual");
      setLines([emptyLine(), emptyLine()]);
      setError("");
    }
  }, [open]);

  const accountOptions = accounts.map((a) => ({
    value: a._id,
    label: `${a.code} - ${a.name}`,
  }));

  const parseCents = (val: string): number => {
    const num = parseFloat(val);
    if (isNaN(num) || num < 0) return 0;
    return Math.round(num * 100);
  };

  const totalDebits = lines.reduce((sum, l) => sum + parseCents(l.debit), 0);
  const totalCredits = lines.reduce((sum, l) => sum + parseCents(l.credit), 0);
  const isBalanced = totalDebits === totalCredits && totalDebits > 0;

  const formatCurrency = (cents: number): string => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);
  };

  const updateLine = (index: number, field: keyof JournalLineForm, value: string) => {
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, [field]: value } : line))
    );
  };

  const addLine = () => {
    setLines((prev) => [...prev, emptyLine()]);
  };

  const removeLine = (index: number) => {
    if (lines.length <= 2) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setError("");

    // Validate all lines have an account
    const incompleteLines = lines.some((l) => !l.accountId);
    if (incompleteLines) {
      setError("Every line must have an account selected.");
      return;
    }

    // Validate each line has either a debit or credit (not both zero)
    const emptyAmountLines = lines.some(
      (l) => parseCents(l.debit) === 0 && parseCents(l.credit) === 0
    );
    if (emptyAmountLines) {
      setError("Every line must have a debit or credit amount.");
      return;
    }

    if (!isBalanced) {
      setError("Total debits must equal total credits.");
      return;
    }

    setSubmitting(true);
    try {
      await journalEntriesApi.create({
        memo: memo || null,
        source: { type: sourceType, id: crypto.randomUUID() },
        lines: lines.map((l) => ({
          accountId: l.accountId,
          debitCents: parseCents(l.debit),
          creditCents: parseCents(l.credit),
          memo: l.memo || null,
        })),
      });
      onCreated();
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to create journal entry.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-between">
      <div className="text-xs text-slate-500">
        {isBalanced ? (
          <span className="text-emerald-600 font-medium">Entry is balanced</span>
        ) : totalDebits > 0 || totalCredits > 0 ? (
          <span className="text-red-500 font-medium">
            Difference: {formatCurrency(Math.abs(totalDebits - totalCredits))}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !isBalanced}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: submitting || !isBalanced ? undefined : "var(--color-primary)" }}
          onMouseEnter={(e) => {
            if (!submitting && isBalanced)
              (e.target as HTMLButtonElement).style.backgroundColor = "var(--color-primary-hover)";
          }}
          onMouseLeave={(e) => {
            if (!submitting && isBalanced)
              (e.target as HTMLButtonElement).style.backgroundColor = "var(--color-primary)";
          }}
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Posting...
            </>
          ) : (
            "Post Entry"
          )}
        </button>
      </div>
    </div>
  );

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title="New Journal Entry"
      subtitle="Create a manual journal entry"
      icon={BookOpen}
      size="xl"
      footer={footer}
    >
      <div className="space-y-5">
        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Header fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Memo</label>
            <input
              type="text"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="e.g. Month-end adjustment"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Source Type</label>
            <SearchableSelect
              options={sourceTypeOptions}
              value={sourceType}
              onChange={(val) => setSourceType(val || "manual")}
              placeholder="Select type"
            />
          </div>
        </div>

        {/* Lines header */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-slate-700">Journal Lines</label>
            <button
              type="button"
              onClick={addLine}
              className="inline-flex items-center gap-1.5 text-xs font-medium transition-colors"
              style={{ color: "var(--color-primary)" }}
              onMouseEnter={(e) =>
                ((e.target as HTMLButtonElement).style.color = "var(--color-primary-hover)")
              }
              onMouseLeave={(e) =>
                ((e.target as HTMLButtonElement).style.color = "var(--color-primary)")
              }
            >
              <Plus className="h-3.5 w-3.5" />
              Add Line
            </button>
          </div>

          {/* Column labels */}
          <div className="grid grid-cols-[1fr_120px_120px_1fr_32px] gap-2 mb-1 px-1">
            <span className="text-xs text-slate-500">Account</span>
            <span className="text-xs text-slate-500">Debit ($)</span>
            <span className="text-xs text-slate-500">Credit ($)</span>
            <span className="text-xs text-slate-500">Line Memo</span>
            <span></span>
          </div>

          {/* Lines */}
          <div className="space-y-2">
            {lines.map((line, idx) => (
              <div
                key={idx}
                className="grid grid-cols-[1fr_120px_120px_1fr_32px] gap-2 items-start"
              >
                <SearchableSelect
                  options={accountOptions}
                  value={line.accountId || null}
                  onChange={(val) => updateLine(idx, "accountId", val || "")}
                  placeholder="Select account"
                  loading={loadingAccounts}
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.debit}
                  onChange={(e) => updateLine(idx, "debit", e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm text-right focus:outline-none focus:border-emerald-600"
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.credit}
                  onChange={(e) => updateLine(idx, "credit", e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm text-right focus:outline-none focus:border-emerald-600"
                />
                <input
                  type="text"
                  value={line.memo}
                  onChange={(e) => updateLine(idx, "memo", e.target.value)}
                  placeholder="Optional"
                  className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm focus:outline-none focus:border-emerald-600"
                />
                <button
                  type="button"
                  onClick={() => removeLine(idx)}
                  disabled={lines.length <= 2}
                  className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent mt-0.5"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Totals row */}
        <div className="border-t border-slate-200 pt-3">
          <div className="grid grid-cols-[1fr_120px_120px_1fr_32px] gap-2 px-1">
            <span className="text-xs font-semibold text-slate-700">Totals</span>
            <span
              className={`text-sm font-bold text-right ${
                totalDebits > 0 && isBalanced ? "text-emerald-700" : "text-slate-800"
              }`}
            >
              {formatCurrency(totalDebits)}
            </span>
            <span
              className={`text-sm font-bold text-right ${
                totalCredits > 0 && isBalanced ? "text-emerald-700" : "text-slate-800"
              }`}
            >
              {formatCurrency(totalCredits)}
            </span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    </PremiumModal>
  );
};

export default NewJournalEntryModal;
